import { select } from "d3-selection"
import { linkVertical } from "d3-shape"
import "d3-transition"
import { SPOUSE_GAP } from "./layout"

const DURATION = 300
const CARD = { w: 120, h: 90, r: 14 }
const AVATAR_R = 18

let stateLabelsMap = {}

export function setStateLabels(labels) {
  stateLabelsMap = labels || {}
}

export function renderNodes(g, root, options = {}) {
  const nodes = root.descendants()
  const allNodes = collectAllNodes(nodes)
  const orphanNodes = collectOrphanSubtreeNodes(root)

  const combined = [...allNodes, ...orphanNodes]

  const nodeGroup = g.selectAll(".tree-node")
    .data(combined, d => d.nodeKey)

  const enter = nodeGroup.enter()
    .append("g")
    .attr("class", d => nodeClasses(d))
    .attr("transform", d => `translate(${d._x ?? d.x},${d._y ?? d.y})`)
    .attr("role", "button")
    .attr("tabindex", "0")
    .attr("data-person-id", d => personIdFromNodeKey(d.nodeKey))
    .attr("data-node-key", d => d.nodeKey)
    .attr("aria-label", d => {
      const label = stateLabel(d.state)
      return label ? `${d.displayName} — ${label}` : d.displayName
    })
    .style("opacity", 0)

  renderCardNode(enter)

  enter.transition()
    .duration(DURATION)
    .style("opacity", 1)

  nodeGroup.exit().transition()
    .duration(DURATION)
    .style("opacity", 0)
    .remove()

  return enter.merge(nodeGroup)
}

function nodeClasses(d) {
  let cls = `tree-node tree-node--${d.state}`
  if (d.isElder) cls += " tree-node--elder"
  if (d.isCurrentUser) cls += " tree-node--me"
  if (d.gender) cls += ` tree-node--${d.gender}`
  return cls
}

function personIdFromNodeKey(nodeKey) {
  if (!nodeKey || typeof nodeKey !== "string") return null
  const match = nodeKey.match(/^p(\d+)$/)
  return match ? match[1] : null
}

function collectAllNodes(hierarchyNodes) {
  const all = []
  for (const node of hierarchyNodes) {
    const d = node.data.data || node.data
    const hasChildren = !!(node.data.children?.length || node.data._children?.length)
    const collapsed = !!node.data._children?.length
    const collapseButtons = buildCollapseButtons(node)
    all.push({
      ...d, x: node.x, y: node.y,
      _hasChildren: hasChildren, _collapsed: collapsed,
      _collapseButtons: collapseButtons,
      _parentNodeKey: node.data.nodeKey
    })

    if (node.data.spouses) {
      for (const spouse of node.data.spouses) {
        all.push({ ...spouse, x: spouse._x, y: spouse._y, _x: spouse._x, _y: spouse._y })
      }
    }
  }
  return all
}

// Compute one collapse button per union with children, anchored at the
// MIDPOINT between the two spouses of the union. Loose children (no union)
// fall back to a centered button under the parent node.
function buildCollapseButtons(node) {
  const buttons = []
  const spouses = node.data.spouses || []
  const allChildren = [...(node.data.children || []), ...(node.data._children || [])]
  const collapsedByUnion = node.data._collapsedByUnion

  for (const spouse of spouses) {
    if (!spouse.unionKey) continue
    const collapsed = collapsedByUnion?.has(spouse.unionKey)
    const liveChildrenForUnion = allChildren.filter(c => c._unionKey === spouse.unionKey)
    if (!liveChildrenForUnion.length && !collapsed) continue

    const midX = (node.x + spouse._x) / 2
    buttons.push({
      unionKey: spouse.unionKey,
      anchorX: midX - node.x,
      collapsed: !!collapsed
    })
  }

  // Loose children (no union) → centered button
  const looseChildren = allChildren.filter(c => !c._unionKey)
  if (looseChildren.length) {
    buttons.push({
      unionKey: null,
      anchorX: 0,
      collapsed: !!node.data._children?.length
    })
  }

  return buttons
}

function collectOrphanSubtreeNodes(root) {
  if (!root._orphanSubtrees?.length) return []
  const all = []
  for (const sh of root._orphanSubtrees) {
    all.push(...collectAllNodes(sh.descendants()))
  }
  return all
}

function ensureGradientDefs(selection) {
  const svg = selection.node()?.ownerSVGElement
  if (!svg || svg.querySelector("#patriarch-ring-gradient")) return

  const defs = select(svg).append("defs")
  const grad = defs.append("linearGradient")
    .attr("id", "patriarch-ring-gradient")
    .attr("x1", "0%").attr("y1", "0%")
    .attr("x2", "100%").attr("y2", "100%")
  grad.append("stop").attr("offset", "0%").attr("stop-color", "#059669")
  grad.append("stop").attr("offset", "40%").attr("stop-color", "#047857")
  grad.append("stop").attr("offset", "75%").attr("stop-color", "#b91c3c")
  grad.append("stop").attr("offset", "100%").attr("stop-color", "#b91c3c")
}

function renderCardNode(selection) {
  const hw = CARD.w / 2, hh = CARD.h / 2

  ensureGradientDefs(selection)

  // Placeholder "?" nodes (AC2 — guest contribution zones)
  const placeholders = selection.filter(d => d.state === "placeholder")
  renderPlaceholderNode(placeholders)

  // Regular card nodes
  const regular = selection.filter(d => d.state !== "placeholder")

  // Card background
  regular.append("rect")
    .attr("x", -hw).attr("y", -hh)
    .attr("width", CARD.w).attr("height", CARD.h)
    .attr("rx", CARD.r).attr("ry", CARD.r)
    .attr("class", "tree-node__card")

  // Inner ring (glass depth)
  regular.append("rect")
    .attr("x", -hw + 2).attr("y", -hh + 2)
    .attr("width", CARD.w - 4).attr("height", CARD.h - 4)
    .attr("rx", CARD.r - 1).attr("ry", CARD.r - 1)
    .attr("class", "tree-node__inner-ring")

  // Avatar circle (centered top, larger)
  regular.append("circle")
    .attr("cx", 0).attr("cy", -hh + 24)
    .attr("r", AVATAR_R)
    .attr("class", "tree-node__avatar")

  // Avatar ring (gradient border effect via stroke)
  regular.append("circle")
    .attr("cx", 0).attr("cy", -hh + 24)
    .attr("r", AVATAR_R + 2)
    .attr("class", "tree-node__avatar-ring")

  // Initials
  regular.append("text")
    .attr("x", 0).attr("y", -hh + 28)
    .attr("text-anchor", "middle")
    .attr("class", "tree-node__initials")
    .text(d => initials(d))

  // Crown emoji for elder (doyen/ne)
  regular.filter(d => d.isElder)
    .append("text")
    .attr("x", 0).attr("y", -hh + 2)
    .attr("text-anchor", "middle")
    .attr("class", "tree-node__crown")
    .text("👑")

  // Display name (centered below avatar)
  regular.append("text")
    .attr("x", 0).attr("y", 8)
    .attr("text-anchor", "middle")
    .attr("class", "tree-node__name")
    .text(d => truncateName(d.displayName, 18))

  // Village
  regular.filter(d => d.villageOfOrigin)
    .append("text")
    .attr("x", 0).attr("y", 22)
    .attr("text-anchor", "middle")
    .attr("class", "tree-node__meta")
    .text(d => truncateName(d.villageOfOrigin, 16))

  // Year
  regular.filter(d => d.bornYear)
    .append("text")
    .attr("x", 0).attr("y", d => d.villageOfOrigin ? 34 : 22)
    .attr("text-anchor", "middle")
    .attr("class", "tree-node__meta")
    .text(d => d.diedYear ? `${d.bornYear}–${d.diedYear}` : String(d.bornYear))

  // Portal badge (top-right corner)
  regular.filter(d => d.hasPortal)
    .append("circle")
    .attr("cx", hw - 8).attr("cy", -hh + 8)
    .attr("r", 5)
    .attr("class", "tree-node__portal-badge")

  // Collapse/expand buttons (one per union with children, anchored under the wife)
  renderCollapseButtons(regular, hh)
}

function renderCollapseButtons(selection, hh) {
  selection.each(function (d) {
    const buttons = d._collapseButtons || []
    if (!buttons.length) return
    const g = select(this)
    for (const btn of buttons) {
      g.append("circle")
        .attr("cx", btn.anchorX).attr("cy", hh + 10)
        .attr("r", 8)
        .attr("class", "tree-node__collapse-btn")
        .attr("data-union-key", btn.unionKey || "")
        .attr("data-node-key", d._parentNodeKey)
        .attr("data-anchor-x", btn.anchorX)

      g.append("text")
        .attr("x", btn.anchorX).attr("y", hh + 14)
        .attr("text-anchor", "middle")
        .attr("class", "tree-node__collapse-icon")
        .text(btn.collapsed ? "+" : "−")
        .style("font-size", "12px")
        .style("pointer-events", "none")
    }
  })
}

const PLACEHOLDER_R = 24

function renderPlaceholderNode(selection) {
  // Outer circle — 48×48 touch target (WCAG AA ≥44px)
  selection.append("circle")
    .attr("cx", 0).attr("cy", 0)
    .attr("r", PLACEHOLDER_R)
    .attr("class", "tree-node__placeholder")

  // Pulse animation ring
  selection.append("circle")
    .attr("cx", 0).attr("cy", 0)
    .attr("r", PLACEHOLDER_R)
    .attr("class", "tree-node__placeholder-pulse")

  // "?" text
  selection.append("text")
    .attr("x", 0).attr("y", 6)
    .attr("text-anchor", "middle")
    .attr("class", "tree-node__placeholder-text")
    .text("?")
    .style("font-size", "20px")
    .style("font-weight", "bold")
    .style("pointer-events", "none")
}

function initials(d) {
  const f = d.firstName?.[0] || d.manjakFirstName?.[0] || ""
  const l = d.lastName?.[0] || d.manjakLastName?.[0] || ""
  return (f + l).toUpperCase() || "?"
}

export function renderLinks(g, root, data) {
  const paths = []

  collectFamilyPaths(root, paths)
  if (root._orphanSubtrees) {
    for (const sh of root._orphanSubtrees) {
      collectFamilyPaths(sh, paths)
    }
  }

  g.selectAll(".tree-link")
    .data(paths, d => d.key)
    .join("path")
    .attr("class", d => `tree-link ${d.cls}`)
    .attr("d", d => d.d)
    .attr("fill", "none")
}

const R = 14 // global corner radius
const CARD_H = 45 // half card height

function collectFamilyPaths(root, paths) {
  root.each(node => {
    const spouses = node.data.spouses || []
    const children = node.children || []
    if (!children.length) return

    const childMap = new Map()
    for (const c of children) childMap.set(c.data.nodeKey, c)

    // Union lines: individual segments between consecutive cards
    if (spouses.length) {
      const chain = [{ x: node.x, y: node.y, key: node.data.nodeKey }]
      for (const s of spouses) chain.push({ x: s._x, y: s._y, key: s.nodeKey })
      for (let i = 0; i < chain.length - 1; i++) {
        paths.push({
          key: `u-${chain[i].key}-${chain[i + 1].key}`,
          cls: "tree-link--union",
          d: `M${chain[i].x},${chain[i].y}L${chain[i + 1].x},${chain[i + 1].y}`
        })
      }
    }

    // Build union groups: each spouse's children
    const groups = []
    const claimed = new Set()

    spouses.forEach((spouse, spIdx) => {
      const uKeys = spouse._unionChildKeys || []
      const kids = uKeys.map(ck => childMap.get(ck)).filter(Boolean)
      for (const k of kids) claimed.add(k.data.nodeKey)
      if (!kids.length) return

      // Stem from the MIDPOINT of the couple (centered between both spouses)
      const midX = (node.x + spouse._x) / 2
      groups.push({ anchorX: midX, kids, key: spouse.unionKey, level: spIdx })
    })

    // Loose children (no union) — attach to parent position
    const loose = children.filter(c => !claimed.has(c.data.nodeKey))
    if (loose.length) {
      groups.push({ anchorX: node.x, kids: loose, key: `loose-${node.data.nodeKey}` })
    }

    // Draw each group with staggered junction Y to avoid overlapping elbows
    const topY = node.y + CARD_H
    const childTopY = children[0].y - CARD_H
    const totalRange = childTopY - topY

    groups.forEach((group, idx) => {
      const { anchorX, kids, key, level } = group
      const groupCenterX = (kids[0].x + kids[kids.length - 1].x) / 2
      const levelCls = level != null ? ` tree-link--union-level-${level}` : ""

      // Stagger junction Y: each group gets a different level (20%, 35%, 50%...)
      const fraction = groups.length === 1 ? 0.4 : 0.2 + (idx * 0.15)
      const junctionY = topY + totalRange * Math.min(fraction, 0.6)

      // Elbow: anchor → down → horizontal to group center
      paths.push({
        key: `${key}-elbow`,
        cls: `tree-link--parentage${levelCls}`,
        d: `M${anchorX},${topY}L${anchorX},${junctionY}L${groupCenterX},${junctionY}`
      })

      // Stem + bracket to children
      drawCoupleToChildren(paths, key, groupCenterX, groupCenterX, junctionY, childTopY, kids, level)
    })
  })
}

// Draws: vertical stem from couple midpoint → sibling bracket → children
//   [Parent] ——— [Spouse]
//          |               ← stem from midpoint of union line
//      ╭───┼───╮           ← sibling bracket (if multiple)
//      │   │   │
function drawCoupleToChildren(paths, prefix, x1, x2, topY, childTopY, children, level) {
  const midX = (x1 + x2) / 2
  const levelCls = level != null ? ` tree-link--union-level-${level}` : ""

  if (children.length === 1) {
    paths.push({
      key: `${prefix}-drop`,
      cls: `tree-link--parentage${levelCls}`,
      d: `M${midX},${topY}L${children[0].x},${childTopY}`
    })
  } else {
    const bracketY = topY + (childTopY - topY) / 2
    paths.push({
      key: `${prefix}-stem`,
      cls: `tree-link--parentage${levelCls}`,
      d: `M${midX},${topY}L${midX},${bracketY}`
    })
    drawSiblingBracket(paths, prefix, midX, bracketY, childTopY, children, level)
  }
}

// Draws: ╭────┬────╮ rounded bracket from stem to children
//        │    │    │
function drawSiblingBracket(paths, prefix, stemX, stemY, childTopY, children, level) {
  const levelCls = level != null ? ` tree-link--union-level-${level}` : ""
  const xs = children.map(c => c.x).sort((a, b) => a - b)
  const leftX = xs[0]
  const rightX = xs[xs.length - 1]
  const r = Math.min(R, (rightX - leftX) / 2, childTopY - stemY)

  // Horizontal bar with rounded ends: ╭───────╮
  paths.push({
    key: `${prefix}-bar`,
    cls: `tree-link--parentage${levelCls}`,
    d: `M${leftX},${stemY + r}` +
       `Q${leftX},${stemY} ${leftX + r},${stemY}` +
       `L${rightX - r},${stemY}` +
       `Q${rightX},${stemY} ${rightX},${stemY + r}`
  })

  // Vertical drop from bar to each child
  for (const child of children) {
    const cx = child.x
    // If this child is at the edge, the bar already curves down — start from stemY + r
    // If in the middle, start from stemY
    const startY = (cx === leftX || cx === rightX) ? stemY + r : stemY
    paths.push({
      key: `${prefix}-c-${child.data.nodeKey}`,
      cls: `tree-link--parentage${levelCls}`,
      d: `M${cx},${startY}L${cx},${childTopY}`
    })
  }

  // Connect stem to bar (if stem is between left and right)
  if (stemX > leftX && stemX < rightX) {
    paths.push({
      key: `${prefix}-stem-join`,
      cls: "tree-link--parentage",
      d: `M${stemX},${stemY}L${stemX},${stemY}`
    })
  }
}

export function updateStates(g, selectedKey) {
  g.selectAll(".tree-node")
    .classed("tree-node--selected", d => d.nodeKey === selectedKey)
}

function truncateName(name, maxLen) {
  if (!name) return ""
  return name.length > maxLen ? name.slice(0, maxLen - 1) + "…" : name
}

function stateLabel(state) {
  return stateLabelsMap[state] || ""
}
